/**
 * Device / platform detection utilities.
 *
 * Screen size alone is unreliable (tablets, landscape phones, resized desktop
 * windows, etc.), so we combine multiple signals:
 *   1. `navigator.userAgentData.mobile` – modern Client Hints API (Chromium)
 *   2. User-Agent regex – fallback for Safari / Firefox / older browsers
 *   3. Touch capability – `maxTouchPoints`
 *   4. Coarse pointer media query – `(pointer: coarse)`
 *
 * A device counts as "mobile" if the UA-Data / regex identifies a handset OR
 * if it is a touch device with a coarse pointer AND a narrow viewport.
 */

interface UserAgentDataLike {
  mobile?: boolean;
  platform?: string;
}

function getUserAgentData(): UserAgentDataLike | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as Navigator & { userAgentData?: UserAgentDataLike })
    .userAgentData;
}

const MOBILE_UA_RE =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Mobi/i;
const IOS_UA_RE = /iPhone|iPad|iPod/i;
const ANDROID_UA_RE = /Android/i;

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const uaData = getUserAgentData();
  if (uaData?.platform && /iOS|iPhone|iPad/i.test(uaData.platform)) return true;
  if (IOS_UA_RE.test(ua)) return true;
  // Modern iPads identify as macOS + touch-capable
  if (
    ua.includes("Macintosh") &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

export function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = getUserAgentData();
  if (uaData?.platform && /Android/i.test(uaData.platform)) return true;
  return ANDROID_UA_RE.test(navigator.userAgent || "");
}

export function isMobileDevice(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const uaData = getUserAgentData();
  if (typeof uaData?.mobile === "boolean") {
    return uaData.mobile;
  }

  const ua = navigator.userAgent || "";
  if (MOBILE_UA_RE.test(ua)) return true;
  if (isIOS() || isAndroid()) return true;

  // Final fallback: coarse pointer + touch + narrow viewport
  const hasTouch =
    typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 0;
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.innerWidth <= 820;
  return hasTouch && coarsePointer && narrow;
}
